import {
  Box,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
} from '@mui/material';
import DataExportsTableRow from './DataExportsTableRow';
import { IDataExport } from '../../types/my-types';
import { useNavigate, useLocation } from 'react-router';

import { standardTableStyles } from '../common/tableStyles';

interface DataExportsTableProps {
  exports: IDataExport[];
  loading: boolean;
  isFetching: boolean;
  previousPageDoc: () => void;
  nextPageDoc: () => void;
  totalExports: number;
  page: number;
  perPage: number;
}

const DataExportsTable = ({
  exports,
  loading,
  isFetching,
  previousPageDoc,
  nextPageDoc,
  totalExports,
  page,
  perPage,
}: DataExportsTableProps) => {
  const navigate = useNavigate();

  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);

  return (
    <Box sx={standardTableStyles.containerSx}>
      <Table sx={{ ...standardTableStyles.tableSx, tableLayout: 'fixed' }}>
        <TableHead sx={standardTableStyles.tableHeadSx}>
          <TableRow>
            <TableCell sx={{ width: '8%' }}>ID</TableCell>
            <TableCell sx={{ width: '62%' }}>Filters</TableCell>
            <TableCell sx={{ width: '18%' }}>Date</TableCell>
            <TableCell align="center" sx={{ width: '12%' }}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody data-cy="tablebody-data-exports" sx={standardTableStyles.tableBodySx}>
          {loading
            ? Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell>
                    <Skeleton animation="wave" variant="rounded" height={14} width={40} sx={{ borderRadius: '4px' }} />
                  </TableCell>
                  <TableCell>
                    <Skeleton animation="wave" variant="rounded" height={14} width={`${40 + (i * 17) % 45}%`} sx={{ borderRadius: '4px' }} />
                    <Skeleton animation="wave" variant="rounded" height={12} width={`${25 + (i * 13) % 30}%`} sx={{ borderRadius: '4px', mt: 1 }} />
                  </TableCell>
                  <TableCell>
                    <Skeleton animation="wave" variant="rounded" height={14} width={90} sx={{ borderRadius: '4px' }} />
                  </TableCell>
                  <TableCell align="center">
                    <Skeleton animation="wave" variant="circular" width={20} height={20} sx={{ mx: 'auto' }} />
                  </TableCell>
                </TableRow>
              ))
            : null}

          {!loading && exports?.length > 0
            ? exports.map((exportRow: IDataExport) => (
                <DataExportsTableRow key={exportRow.id} exportRow={exportRow} />
              ))
            : null}

          {exports.length === 0 && !loading && !isFetching ? (
            <TableRow>
              <TableCell colSpan={4}>
                <div style={{ display: 'flex', justifyContent: 'center', padding: '10px' }}>
                  No data exports found
                </div>
              </TableCell>
            </TableRow>
          ) : null}
        </TableBody>
      </Table>

      {totalExports > 0 && (
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50, 150]}
          component="div"
          count={totalExports}
          rowsPerPage={perPage}
          page={page}
          backIconButtonProps={{
            'aria-label': 'Previous Page',
          }}
          nextIconButtonProps={{
            'aria-label': 'Next Page',
          }}
          onPageChange={(e, p) => {
            e?.preventDefault();
            if (p > page) {
              nextPageDoc();
            } else {
              previousPageDoc();
            }

            searchParams.set('page', p.toString());
            navigate({
              search: searchParams.toString(),
            });
          }}
          onRowsPerPageChange={(e: any) => {
            searchParams.set('perPage', e.target.value);
            navigate({
              search: searchParams.toString(),
            });
          }}
        />
      )}
    </Box>
  );
};

export default DataExportsTable;
